import React from 'react';
import { Row, Col, Form, Input, Select, Button, Spin, Typography, Divider } from 'antd';
import { apenasDigitosCelular, CELULAR_DIGITOS_BR } from '../../../../utils/MascaraDeCampos';
import { useFormSemContrato, UseFormSemContratoParams } from './useFormSemContrato';

const { Text } = Typography;

/** Mesmo grid de colunas usado nos modais de usuário (md+ = 8 colunas). */
const formGridInputs = {
    xs: { span: 24, offset: 0 },
    sm: { span: 24, offset: 0 },
    md: { span: 8, offset: 0 },
    lg: { span: 8, offset: 0 },
    xl: { span: 8, offset: 0 },
    xxl: { span: 8, offset: 0 },
};

export type FormSemContratoProps = UseFormSemContratoParams & {
    submitLabel?: string;
};

const FormSemContrato: React.FC<FormSemContratoProps> = ({
    initialVendedorId,
    vendedorLocked,
    onSuccess,
    submitLabel = 'Concluir cadastro',
}) => {
    const {
        form,
        loadingInit,
        loadingSubmit,
        loadingPoder,
        loadingVendedores,
        loadingCidades,
        dadosPoderList,
        dadosCargoList,
        dadosVendedoresList,
        dataListCidades,
        msgUsuarioJaExiste,
        vendedorLocked: vendedorBloqueado,
        onChangeInputTelefone,
        onDropdownVisibleChangeCidades,
        handleSearchCidades,
        handleScrollCidades,
        isSelectCidadeOpen,
        buscarCelularSeJaExiste,
        submit,
        removeAcento,
    } = useFormSemContrato({
        initialVendedorId,
        vendedorLocked,
        onSuccess,
    });

    return (
        <Spin spinning={loadingInit}>
            <Form form={form} layout="vertical" onFinishFailed={() => undefined} style={{ maxWidth: '100%' }}>
                {msgUsuarioJaExiste && (
                    <Row>
                        <Col span={24}>
                            <Text style={{ color: '#cf1322' }}>Usuário já existe!</Text>
                            <br />
                            <Text style={{ color: '#cf1322' }}>Confirme os dados para atualizá-lo.</Text>
                            <Divider style={{ margin: '8px 0' }} />
                        </Col>
                    </Row>
                )}

                <Row gutter={[16, 0]}>
                    <Col {...formGridInputs}>
                        <Form.Item
                            rules={[
                                { required: true, message: 'Preencha o telefone!', max: 16 },
                                () => ({
                                    validator(_rule, value) {
                                        return new Promise<void>((resolve, reject) => {
                                            if (value != null && value !== '') {
                                                const digits = apenasDigitosCelular(value);
                                                if (digits.length === CELULAR_DIGITOS_BR) {
                                                    buscarCelularSeJaExiste(digits)
                                                        .then(() => resolve())
                                                        .catch(() => resolve());
                                                } else {
                                                    reject(new Error('Celular inválido'));
                                                }
                                            } else {
                                                reject(new Error(''));
                                            }
                                        });
                                    },
                                }),
                            ]}
                            name="celular"
                            label="Telefone"
                        >
                            <Input
                                type="tel"
                                autoComplete="tel"
                                onChange={onChangeInputTelefone}
                                placeholder="(44) 9 9999-9999"
                            />
                        </Form.Item>
                    </Col>
                    <Col {...formGridInputs}>
                        <Form.Item
                            rules={[
                                { required: true, message: 'Preencha o nome.' },
                                () => ({
                                    validator(_rule, value) {
                                        if (value?.trim() != null && value?.trim() !== '') {
                                            return Promise.resolve();
                                        }
                                        return Promise.reject(new Error('Preencha o nome!'));
                                    },
                                }),
                            ]}
                            name="nome"
                            label="Nome completo"
                        >
                            <Input
                                autoComplete="name"
                                readOnly={msgUsuarioJaExiste}
                                disabled={msgUsuarioJaExiste}
                                placeholder="Seu nome"
                            />
                        </Form.Item>
                    </Col>
                    <Col {...formGridInputs}>
                        <Form.Item
                            name="cidade_id"
                            label="Cidade"
                            rules={[{ required: true, message: 'Selecione a cidade!' }]}
                        >
                            <Select
                                showSearch
                                placeholder="Busque e selecione uma cidade"
                                onSearch={handleSearchCidades}
                                onPopupScroll={handleScrollCidades}
                                onDropdownVisibleChange={onDropdownVisibleChangeCidades}
                                loading={loadingCidades}
                                filterOption={false}
                                style={{ width: '100%' }}
                                open={isSelectCidadeOpen}
                            >
                                {dataListCidades.map((item) => (
                                    <Select.Option key={item.id} value={item.id}>
                                        {item.nome} - {item?.uf}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col {...formGridInputs}>
                        <Form.Item
                            name="poder_id"
                            label="Poder"
                            rules={[{ required: true, message: 'Selecione o poder!' }]}
                        >
                            <Select
                                showSearch
                                loading={loadingPoder}
                                notFoundContent={loadingPoder ? <Spin size="small" /> : null}
                                optionFilterProp="children"
                                placeholder="Selecione..."
                                filterOption={(input, option: any) => {
                                    const textDigit = removeAcento(input);
                                    const list = removeAcento(option?.children);
                                    return list.indexOf(textDigit) >= 0;
                                }}
                            >
                                {dadosPoderList.map((item) => (
                                    <Select.Option value={item.id} key={item.id}>
                                        {item.nome}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col {...formGridInputs}>
                        <Form.Item name="vendedor_id" label="Vendedor">
                            <Select
                                showSearch
                                allowClear={!vendedorBloqueado}
                                disabled={vendedorBloqueado}
                                optionFilterProp="children"
                                placeholder="Selecione..."
                                loading={loadingVendedores}
                                notFoundContent={loadingVendedores ? <Spin size="small" /> : null}
                                filterOption={(input, option: any) => {
                                    const textDigit = removeAcento(input);
                                    const list = removeAcento(option?.children);
                                    return list.indexOf(textDigit) >= 0;
                                }}
                            >
                                {dadosVendedoresList.map((item) => (
                                    <Select.Option value={item.id} key={item.id}>
                                        {item.nome}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col {...formGridInputs}>
                        <Form.Item
                            name="cargo_id"
                            label="Cargo"
                            rules={[{ required: true, message: 'Selecione o cargo!' }]}
                        >
                            <Select
                                showSearch
                                optionFilterProp="children"
                                placeholder="Selecione..."
                                filterOption={(input, option: any) => {
                                    const textDigit = removeAcento(input);
                                    const list = removeAcento(option?.children);
                                    return list.indexOf(textDigit) >= 0;
                                }}
                            >
                                {dadosCargoList.map((item) => (
                                    <Select.Option value={item.id} key={item.id}>
                                        {item.nome}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                </Row>
                <Row style={{ marginTop: 8 }}>
                    <Col span={24}>
                        <Button type="primary" size="large" block loading={loadingSubmit} onClick={submit}>
                            {submitLabel}
                        </Button>
                    </Col>
                </Row>
            </Form>
        </Spin>
    );
};

export default FormSemContrato;
